

import React from 'react';
import { styled, ThemeProvider, createTheme, alpha, keyframes, useTheme } from '@mui/material/styles';
import StagePath from 'components/v2/StagePath';
import {
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  CssBaseline,
} from '@mui/material';
import colors from 'styles/tokens/colors';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: colors.brand.primary,
      light: alpha(colors.brand.primary, 0.2),
    },
    success: {
      main: '#4CAF50',
      light: alpha('#4CAF50', 0.2),
    },
    error: {
      main: '#F44335',
      light: alpha('#F44335', 0.2),
    },
    background: {
      default: '#f0f2f5',
    },
    text: {
      primary: colors.text.dark,
      secondary: colors.text.light,
      completed: colors.text.dark,
    },
    grey: {
      main: '#9e9e9e',
      light: alpha('#9e9e9e', 0.2),
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Hanken Grotesk", "Inter", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'rgba(0, 0, 0, 0.6)', // Lighter black for default state
          '&.Mui-completed': {
            color: 'rgba(0, 0, 0, 0.7)', // Darker shade for completed state
            fontWeight: 600, // Slightly bolder for completed state
          },
        },
      },
    },
  },
});

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${alpha(colors.brand.primary, 0.4)};
  }
  70% {
    box-shadow: 0 0 0 10px ${alpha(colors.brand.primary, 0)};
  }
  100% {
    box-shadow: 0 0 0 0 ${alpha(colors.brand.primary, 0)};
  }
`;

const StepperWrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
}));


const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  top: 22,
  left: 'calc(-50% + 20px)',
  right: 'calc(50% + 20px)',
  '& .MuiStepConnector-line': {
    borderTopWidth: 3,
    borderRadius: 1,
    borderColor: theme.palette.grey[300],
  },
  '&.Mui-active, &.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const CustomStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.common.white,
  zIndex: 1,
  color: theme.palette.text.secondary,
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  border: `2px solid ${theme.palette.grey[300]}`,
  transition: theme.transitions.create(
    ['border-color', 'background-color', 'color', 'box-shadow', 'transform'],
    {
      duration: 200,
    }
  ),
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  ...(ownerState.active && {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
    animation: `${pulseAnimation} 2s infinite`,
    '&:hover': {
      animation: 'none',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  }),
  ...(ownerState.completed && {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      transform: 'scale(1.05)',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  }),
  ...(ownerState.success && {
    backgroundColor: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    color: theme.palette.common.white,
    boxShadow: `0 0 0 4px ${theme.palette.success.light}`,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
      transform: 'scale(1.05)',
      boxShadow: `0 0 0 3px ${theme.palette.success.light}`,
    },
  }),
  ...(ownerState.failure && {
    backgroundColor: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    color: theme.palette.common.white,
    boxShadow: `0 0 0 4px ${theme.palette.error.light}`,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
      transform: 'scale(1.05)',
      boxShadow: `0 0 0 3px ${theme.palette.error.light}`,
    },
  }),
  ...(ownerState.greyedOut && {
    backgroundColor: theme.palette.grey.main,
    borderColor: theme.palette.grey.main,
    color: theme.palette.common.white,
    boxShadow: 'none',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: theme.palette.grey.main,
      transform: 'none',
      cursor: 'not-allowed',
    },
  }),
}));

const CustomStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
    transition: theme.transitions.create(['color', 'transform'], {
      duration: 200,
    }),
  },
  '&.MuiStepLabel-root.Mui-active': {
    '& .MuiStepLabel-label': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      transform: 'scale(1.02)',
    },
    '& .MuiStepLabel-iconContainer': {
      transform: 'scale(1.05)',
    },
  },
  '&.MuiStepLabel-root.Mui-completed .MuiStepLabel-label': {
    color: theme.palette.text.completed,
    fontWeight: 600,
  },
  '&:hover': {
    cursor: 'pointer',
    '& .MuiStepLabel-label': {
      color: theme.palette.primary.main,
      transform: 'scale(1.02)',
    },
    '& .MuiStepLabel-iconContainer': {
      transform: 'scale(1.05)',
      background: alpha(theme.palette.primary.main, 0.08),
      borderRadius: '50%',
    },
  },
  '& .MuiStepLabel-iconContainer': {
    transition: theme.transitions.create(['transform', 'background'], {
      duration: 200,
    }),
  },
  '&.MuiStepLabel-root.Mui-completed:hover .MuiStepLabel-label': {
    color: theme.palette.text.completed,
  },
}));

function CustomStepIcon(props) {
  const { 
    active, 
    completed, 
    className, 
    finalState, 
    successStep, 
    failureStep,
    currentStepLabel,
  } = props;

  // Only show success/failure colors if a final state is selected
  const isSuccess = finalState && currentStepLabel === successStep && finalState === successStep;
  const isFailure = finalState && currentStepLabel === failureStep && finalState === failureStep;
  
  // Grey out the other final step when one is selected
  const isGreyedOut = finalState && (
    (currentStepLabel === successStep && finalState === failureStep) ||
    (currentStepLabel === failureStep && finalState === successStep)
  );

  return (
    <CustomStepIconRoot 
      ownerState={{ 
        active, 
        completed, 
        success: isSuccess, 
        failure: isFailure,
        greyedOut: isGreyedOut
      }} 
      className={className}
    >
      {props.icon}
    </CustomStepIconRoot>
  );
}
  
  // function LeadStatusStepper({ activeStep, steps }) {
  //   return (
  //     <StepperWrapper>
  //       <Stepper alternativeLabel activeStep={activeStep} connector={<CustomStepConnector />}>
  //         {steps.map((label) => (
  //           <Step key={label}>
  //             <CustomStepLabel StepIconComponent={CustomStepIcon}>{label}</CustomStepLabel>
  //           </Step>
  //         ))}
  //       </Stepper>
  //     </StepperWrapper>
  //   );
  // }

  // function LeadStatusStepper({ activeStep, steps, onStepClick }) {
  //   return (
  //     <StepperWrapper>
  //       <Stepper alternativeLabel activeStep={activeStep} connector={<CustomStepConnector />}>
  //         {steps.map((label, index) => (
  //           <Step key={label} onClick={() => onStepClick(index)}>
  //             <CustomStepLabel StepIconComponent={CustomStepIcon}>{label}</CustomStepLabel>
  //           </Step>
  //         ))}
  //       </Stepper>
  //     </StepperWrapper>
  //   );
  // }
  
  function LeadStatusStepper({ 
    activeStep, 
    steps, 
    onStepClick, 
    finalState,
    successStep,
    failureStep 
  }) {
    return (
      <StepperWrapper>
        <Stepper alternativeLabel activeStep={activeStep} connector={<CustomStepConnector />}>
          {steps.map((label, index) => {
            // Grey out the opposite final step when one is selected
            const isGreyedOut = finalState && (
              (label === successStep && finalState === failureStep) ||
              (label === failureStep && finalState === successStep)
            );
            
            return (
              <Step 
                key={label} 
                onClick={!isGreyedOut && onStepClick ? () => onStepClick(index) : undefined}
                style={{ 
                  cursor: isGreyedOut ? 'not-allowed' : onStepClick ? 'pointer' : 'default',
                  opacity: isGreyedOut ? 0.7 : 1
                }}
              >
                <CustomStepLabel 
                  StepIconComponent={(props) => (
                    <CustomStepIcon 
                      {...props} 
                      finalState={finalState}
                      successStep={successStep}
                      failureStep={failureStep}
                      currentStepLabel={label}
                    />
                  )}
                >
                  {label}
                </CustomStepLabel>
              </Step>
            );
          })}
        </Stepper>
      </StepperWrapper>
    );
  }
  
  
  // Wrap the LeadStatusStepper with ThemeProvider
  // function ThemedCustomStepper({ activeStep, steps }) {
  //   return (
  //     <ThemeProvider theme={theme}>
  //       <CssBaseline />
  //       <LeadStatusStepper activeStep={activeStep} steps={steps} />
  //     </ThemeProvider>
  //   );
  // }

  function ThemedCustomStepper({ 
    activeStep, 
    steps, 
    onStepClick, 
    finalState,
    successStep,
    failureStep 
  }) {
    // UI v2: the design's stage path. Read the app theme here, outside the
    // stepper's own ThemeProvider below.
    const appTheme = useTheme();
    if (appTheme?.pearlUi === 'v2') {
      return (
        <StagePath
          activeStep={activeStep}
          steps={steps}
          onStepClick={onStepClick}
          finalState={finalState}
          successStep={successStep}
          failureStep={failureStep}
        />
      );
    }
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LeadStatusStepper 
          activeStep={activeStep} 
          steps={steps} 
          onStepClick={onStepClick}
          finalState={finalState}
          successStep={successStep}
          failureStep={failureStep}
        />
      </ThemeProvider>
    );
  }
  
  export default ThemedCustomStepper;